import { useEffect, useState } from "react";
import { styled } from "@linaria/react";
import { t } from "@/themes/theme.ts";
import default_icon from "/icons/default_pfp.svg";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useUserStore } from "@/stores/useUserStore.ts";
import { SwitchNavStatus } from "@/components/switches/SwitchNavStatus.tsx";
import { CounterNavCapacity } from "@/components/counters/CounterNavCapacity.tsx";
import { LogoEedi } from "@/logos/LogoEedi.tsx";
import { TutorStatus } from "@/types/tutor.ts";
import { Dropdown, DropdownItem, DropdownLink } from "@/components/common/Dropdown.tsx";
import { useChatStore } from "@/stores/useChatStore.ts";
import { EmojiWave } from "@/components/animations/EmojiWave.tsx";
import { toastAndError } from "@/helpers/toast.ts";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import { ModalAlert } from "@/components/modal/ModalAlert.tsx";
import { ModalSettingsMenu } from "@/components/modal/ModalSettingsMenu.tsx";
import { useActiveSessionStore } from "@/stores/useActiveSessionStore.ts";
import { Column } from "@/components/common/column.ts";
import Rand from "rand-seed";
import { useStateWithDeps } from "use-state-with-deps";

const borderSize = "0.375rem";

const LocalNav = styled.nav`
  width: 100%;
  height: 4rem;
  display: flex;
  background-color: ${t.brandPrimary};
  gap: 0.75rem;
  padding: 0 1rem;

  border-bottom: ${t.brandBorderSegment};
  flex-shrink: 0;

  @container style(--small-height: 1) {
    height: 2.5rem;
  }
`;

const NavA = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  // Hack: border-direction + transparent does not work, and will cause the image to render
  // We need padding on top of offset the text, otherwise it jumps up
  padding: ${borderSize} 1rem 0;

  border: none;
  border-bottom: ${borderSize} solid transparent;
  white-space: nowrap;
  width: 100px;

  height: 100%;
  font-size: var(--15px);

  cursor: pointer;
  text-decoration: none !important;
  color: ${t.brandTextPrimary};

  &:active {
    text-decoration: none;
    color: inherit;
  }

  &[data-active="true"] {
    border-bottom: ${borderSize} solid ${t.navButtonActive};
    font-family: ${t.fontPrimary};
    font-weight: 600;

    &[data-ai="true"] {
      border-image: ${t.brandAiBackground} 1;
    }
  }

  user-select: none;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  gap: 0.75rem;
`;

const PfpIcon = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 100%;
  min-width: 30px;

  &[data-inactive="true"] {
    opacity: 0.5;
  }
`;

const DropdownUserInfo = styled.div`
  padding: 0.5rem 1rem;
  border-bottom: 1px solid ${t.brandBorderColor};
  font-size: var(--14px);
  color: ${t.brandTextPrimary};
`;

const DropdownUserEmail = styled.div`
  font-size: var(--13px);
  color: #64748b;
`;

const LogoWrapper = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  flex-basis: 5rem;
  margin-right: 1.5rem;
  text-decoration: none;
  color: inherit;
  flex-shrink: 0;
`;

const PfpWrapper = styled.div`
  position: relative;
`;

const PfpStatus = styled.div`
  position: absolute;
  border-radius: 9999px;
  width: 10px;
  height: 10px;
  right: 0;
  bottom: 0;
  border: 1px solid ${t.brandNeutralBlack};

  background-color: ${t.brandAccentRed};

  &[data-active="true"] {
    background-color: ${t.brandAccentGreen};
  }
`;

const Separator = styled.div`
  width: 1px;
  height: 30px;
  background-color: #cbd5e1;
`;

const NoSelect = styled.div`
  user-select: none;
`;

const Wave = styled(EmojiWave)`
  top: 20px;
  left: 0;
  font-size: 1.5rem;
`;

const Container = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 2rem;
  user-select: none;
  cursor: pointer;
`;

// TODO: Inherit from DropdownItem (can't be done due to some wyw-in-js bug, can't upgrade either atm)
const DropdownAlert = styled.div`
  display: block;
  padding: 0.5rem 1rem;
  font-size: var(--14px);
  color: ${t.brandTextPrimary};
  cursor: pointer;

  transition: all 0.1s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: ${t.brandAccentRed};
  }
`;

const Counter = styled.div`
  position: absolute;
  display: grid;
  place-items: center;
  top: 2px;
  right: 2px;
  background: ${t.navButtonActive};
  color: ${t.brandNeutralWhite};
  border-radius: 9999px;
  width: 20px;
  height: 20px;

  &[data-flagged="true"] {
    background: ${t.brandAccentRed};
  }
`;

enum ActiveNav {
  None,
  AllChats,
  MyChats,
  Complete,
}

const useActiveNav = () => {
  const location = useRouterState({ select: (s) => s.location });
  const path = location.href;

  if (path.includes("/all-chats")) {
    return ActiveNav.AllChats;
  }

  if (path.includes("/my-chats")) {
    return ActiveNav.MyChats;
  }

  if (path.includes("/complete")) {
    return ActiveNav.Complete;
  }

  return ActiveNav.None;
};

export const Nav = () => {
  const { assignedSessions } = useAssignedChatStore();
  const { tutorDetails, userDetails, settings } = useUserStore();
  const { helpRequestedSessions } = useActiveSessionStore();
  const navigate = useNavigate();
  const { chats } = useChatStore();
  const activeNav = useActiveNav();

  const [now, setNow] = useState(() => Date.now());
  const [showAlert, setShowAlert] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [experimentArmColor, _] = useStateWithDeps(
    "#" + Math.floor(new Rand(tutorDetails?.experimentArm).next() * 16777215).toString(16),
    [tutorDetails?.experimentArm],
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000 * 60);

    return () => window.clearInterval(id);
  }, []);

  const time = () => {
    if (!tutorDetails?.currentRotaActiveTo) return "Not on rota";

    const end = new Date(tutorDetails.currentRotaActiveTo).getTime();
    const remainingMs = end - now;

    if (remainingMs <= 0) return "Not on rota";

    const totalMinutes = Math.floor(remainingMs / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  const unfinishedSessions = [...assignedSessions.values()].filter((session) => !session.completed);

  const unopenedChat = [...chats.values()].filter(
    (chat) =>
      !chat.hasBeenOpened &&
      unfinishedSessions.find(
        (session) => session.assignedChat.flowGeneratorSessionId === chat.flowGeneratorSessionId,
      ),
  );

  return (
    <LocalNav>
      <LogoWrapper to="/" draggable={false}>
        <LogoEedi />
      </LogoWrapper>
      <Left>
        <NavA data-active={activeNav === ActiveNav.AllChats} to={"/all-chats"} draggable={false}>
          All Chats
          {helpRequestedSessions.length > 0 && (
            <Counter>
              <span data-tooltip={"Amount of unclaimed chats"} data-direction={"bottom"}>
                {helpRequestedSessions.length}
              </span>
            </Counter>
          )}
        </NavA>
        <NavA data-active={activeNav === ActiveNav.MyChats} to={"/my-chats"} draggable={false}>
          My Chats
        </NavA>
        <NavA data-active={activeNav === ActiveNav.Complete} to={"/complete"} draggable={false}>
          Complete
        </NavA>
      </Left>
      <Right>
        {unopenedChat.length > 0 && (
          <>
            <Container
              data-tooltip={"Click 'my chats' to view newly assigned students"}
              data-direction={"bottom"}
              onClick={() =>
                navigate({ to: "/my-chats" }).catch((e) =>
                  toastAndError("Could not navigate to my chats", e),
                )
              }
            >
              <Wave>👋</Wave>
              {unopenedChat.length} new student
              {unopenedChat.length === 1 ? "" : "s"}
            </Container>
            <Separator />
          </>
        )}
        <Column data-gap={"none"}>
          {tutorDetails?.experimentArm && (
            <NoSelect
              data-tooltip={"Current experiment arm"}
              data-direction={"bottom"}
              style={{ color: experimentArmColor }}
            >
              {tutorDetails?.experimentArm}
            </NoSelect>
          )}
          <NoSelect
            data-tooltip={
              "Time left on rota. This data may become stale, always refresh if you're unsure."
            }
            data-direction={"bottom"}
          >
            {time()}
          </NoSelect>
        </Column>
        <Separator />
        <CounterNavCapacity />
        <Separator />
        <SwitchNavStatus />
        <Separator />
        <Dropdown
          trigger={
            <PfpWrapper draggable={false}>
              <PfpStatus data-active={settings.status === TutorStatus.Available} />
              <PfpIcon
                draggable={false}
                src={tutorDetails?.imageURL ?? default_icon}
                alt={"Profile menu"}
              />
            </PfpWrapper>
          }
        >
          <DropdownUserInfo>
            {tutorDetails
              ? `${tutorDetails.firstName} ${tutorDetails.lastName}`.trim()
              : userDetails
                ? `${userDetails.firstName} ${userDetails.lastName}`.trim()
                : "Account"}
            {userDetails?.email && <DropdownUserEmail>{userDetails.email}</DropdownUserEmail>}
          </DropdownUserInfo>
          <DropdownAlert onClick={() => setShowAlert(true)}>Request help</DropdownAlert>
          <DropdownItem onClick={() => setShowSettings(true)}>Settings</DropdownItem>
          <DropdownLink to="/auth/logout">Log out</DropdownLink>
        </Dropdown>
      </Right>
      {showAlert && <ModalAlert onClose={() => setShowAlert(false)} />}
      {showSettings && <ModalSettingsMenu onClose={() => setShowSettings(false)} />}
    </LocalNav>
  );
};
